import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Linkedin, ArrowRight } from "lucide-react";
import { LogoMark } from "./Logo";
import { FIRM } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message)
      return toast.error("Please fill in your name, email and message.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been sent.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" className="relative z-10 bg-[#F2EFE9] text-[#050A14]" data-testid="contact">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-[18vw] md:text-[13vw] font-900 tracking-tightest leading-[0.85]"
        >
          LET&apos;S TALK
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xl font-500 leading-relaxed max-w-md">
              Tell us where your business is headed. We&apos;ll show you how a well-run legal function gets you there faster.
            </p>
            <div className="mt-10 space-y-4">
              <a href={`mailto:${FIRM.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-[#050A14] text-[#F2EFE9] flex items-center justify-center"><Mail size={18} /></span>
                <span className="font-500 group-hover:text-[#D95A41] transition-colors">{FIRM.email}</span>
              </a>
              <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-[#050A14] text-[#F2EFE9] flex items-center justify-center"><Phone size={18} /></span>
                <span className="font-500 group-hover:text-[#D95A41] transition-colors">{FIRM.phone}</span>
              </a>
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#050A14] text-[#F2EFE9] flex items-center justify-center shrink-0"><MapPin size={18} /></span>
                <span className="font-500 max-w-xs">{FIRM.address}</span>
              </div>
              <a href={FIRM.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-[#050A14] text-[#F2EFE9] flex items-center justify-center"><Linkedin size={18} /></span>
                <span className="font-500 group-hover:text-[#D95A41] transition-colors">Connect on LinkedIn</span>
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
            <input required placeholder="Full name" data-testid="contact-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white border border-[#050A14]/15 rounded-xl px-5 py-4 text-[#050A14] placeholder:text-[#050A14]/40 focus:border-[#D95A41] outline-none transition-colors" />
            <input required type="email" placeholder="Email" data-testid="contact-email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-white border border-[#050A14]/15 rounded-xl px-5 py-4 text-[#050A14] placeholder:text-[#050A14]/40 focus:border-[#D95A41] outline-none transition-colors" />
            <input placeholder="Company (optional)" data-testid="contact-company"
              value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="md:col-span-2 bg-white border border-[#050A14]/15 rounded-xl px-5 py-4 text-[#050A14] placeholder:text-[#050A14]/40 focus:border-[#D95A41] outline-none transition-colors" />
            <textarea required placeholder="How can we help?" rows={5} data-testid="contact-message"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="md:col-span-2 bg-white border border-[#050A14]/15 rounded-xl px-5 py-4 text-[#050A14] placeholder:text-[#050A14]/40 focus:border-[#D95A41] outline-none transition-colors resize-none" />
            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="md:col-span-2 py-4 rounded-full bg-[#050A14] text-[#F2EFE9] font-700 hover:bg-[#D95A41] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? "Sending…" : <>Send enquiry <ArrowRight size={18} /></>}
            </button>
          </form>
        </div>

        <div className="mt-20 pt-8 border-t border-[#050A14]/15 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-[#050A14]">
            <LogoMark size={28} />
            <span className="font-700 tracking-tight">{FIRM.name}</span>
          </div>
          <p className="text-sm text-[#050A14]/60">© {new Date().getFullYear()} {FIRM.name}. {FIRM.tagline}.</p>
        </div>
      </div>
    </footer>
  );
}
